import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import laws from "../assets/img/HomePage/laws.svg";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import Options from "../components/Options";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { changeTab } from "../redux/TabSlice";
import { getTitleFromAppContent, getTranslation } from "../helpers/utils";
import { trackLawView, trackPageView } from "../helpers/api";
import { setSelectedLaw, getSelectedLaw } from "../redux/OffenceSlice";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme } from "@emotion/react";
import ArrowBack from "../assets/img/HumQadamHome/arrowBack.svg";
import { setBackToGuide } from "../redux/PageSlice";
import { colors } from "../constants/theme";

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    background: "#FFFFFF",
  },
  textColor: {
    color: theme.palette.laws.main,
  },
  bodyText: {
    color: colors.textDark,
    fontSize: "1rem",
    fontWeight: 400,
  },
  input: {
    "&::placeholder": {
      color: theme.palette.laws.main,
    },
  },
  headingColor: {
    color: colors.textDark,
  },

  circle: {
    background: theme.palette.laws.main,
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    marginTop: "8px",
  },

  sectionbg: {
    background: theme.palette.primary.main,
  },

  sectionBgLight: {
    backgroundColor: "#FFE2E3",
  },

  lawsBg: {
    backgroundColor: theme.palette.laws.light,
  },
  contentBg: {
    backgroundColor: colors.backgroundLight,
  },

  lawsTextBg: {
    backgroundColor: theme.palette.laws.main,
  },

  fullScreen: {
    minHeight: "100vh",
  },
  height: {
    maxHeight: "50vh",
  },
}));

function RelevantLaws() {
  const theme = useTheme();
  const classes = useStyles();
  const [openAbout, setOpenAbout] = useState(false);
  const { selectedLaw } = useSelector((state) => state.offence);
  const { province, selProvinceId } = useSelector((state) => state.province);
  const { selDistrictId } = useSelector((state) => state.district);
  const { appContent } = useSelector((state) => state.appContent);
  const { isEnglish, langId, selLanguageId } = useSelector((state) => state.language);
  const { backToGuide } = useSelector((state) => state.page);
  const [penalTitle, setPenalTitle] = useState("");
  const [lawsTitle, setLawsTitle] = useState("");
  const [penalDesTitle, setPenalDesTitle] = useState("");
  const [relLawsTitle, setRelLawsTitle] = useState("");
  const [lawsProvince, setLawsProvince] = useState({});
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => setOpenAbout(false);

  const [tabSelect, setTabSelect] = useState("RelevantLaws");
  const dispatch = useDispatch();

  // Helper to safely extract text from Translation objects
  const getTextFromTranslation = (translation) => {
    if (!translation) return '';
    if (typeof translation === 'string') return translation;
    if (Array.isArray(translation)) {
      const result = getTranslation(translation, langId);
      if (result && typeof result === 'string') return result;
      if (translation.length > 0 && translation[0]?.text && typeof translation[0].text === 'string') {
        return translation[0].text;
      }
      return '';
    }
    if (typeof translation === 'object' && translation.text) {
      return typeof translation.text === 'string' ? translation.text : '';
    }
    return '';
  };

  // Only clear selectedLaw when opening the page from the main Laws list (e.g. home card).
  // When coming from GuideStep (backToGuide), we keep selectedLaw so the law just fetched is shown.
  useEffect(() => {
    if (!backToGuide) {
      dispatch(setSelectedLaw(null));
    }
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch, backToGuide]);

  useEffect(() => {
    trackPageView("Relevant Laws", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // Track law page view for admin dashboard
  useEffect(() => {
    trackLawView("RelevantLaws", {
      province: selProvinceId,
      district: selDistrictId,
      language: selLanguageId,
    });
  }, [tabSelect, selProvinceId, selDistrictId, selLanguageId]);

  // Fetch full law data if selectedLaw only has _id but not full data
  useEffect(() => {
    if (selectedLaw && selectedLaw._id) {
      // Check if we have full data (sections and laws arrays exist)
      const hasFullData = (selectedLaw.sections && Array.isArray(selectedLaw.sections)) || 
                         (selectedLaw.laws && Array.isArray(selectedLaw.laws));
      
      // If we don't have full data, fetch it
      if (!hasFullData) {
        dispatch(getSelectedLaw({ id: selectedLaw._id, langId: selLanguageId }));
      }
    }
  }, [selectedLaw, selLanguageId, dispatch]);

  const getProvince = (id) => {
    if (id == null) return '';
    const idStr = typeof id === 'object' ? id?._id?.toString?.() : String(id);
    for (let i = 0; i < province?.length; i++) {
      const pId = province[i]._id?.toString?.() ?? province[i]._id;
      if (idStr && pId && idStr === pId) {
        const name = province[i].name;
        return getTextFromTranslation(name);
      }
    }
    return '';
  };

  useEffect(() => {
    if (appContent) {
      setPenalTitle(getTitleFromAppContent(appContent, "Penal Code Provisions"));
      setPenalDesTitle(getTitleFromAppContent(appContent, "Sections"));
      setLawsTitle(getTitleFromAppContent(appContent, "Laws"));
      setRelLawsTitle(getTitleFromAppContent(appContent, "Relevant Laws"));
    }
  }, [appContent]);

  useEffect(() => {
    if (selectedLaw) {
      const lawsByProvince = {};

      selectedLaw.laws.forEach((law) => {
        const provinceId = law.province && typeof law.province === 'object'
          ? law.province._id ?? law.province
          : law.province;
        const key = provinceId?.toString?.() ?? provinceId ?? 'other';
        if (!lawsByProvince[key]) {
          lawsByProvince[key] = [];
        }
        lawsByProvince[key].push(law);
      });
      setLawsProvince(lawsByProvince);
    }
  }, [selectedLaw]);

  if (!selectedLaw)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "300px",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  return (
    <div className={`${classes.contentBg} mb-40 flex flex-col justify-between`}>
      <div>
        <Header />
        <div className="flex-col md:flex-row flex justify-between">
          <div className=" block md:hidden mb-5">
            <Options handleOpen={handleOpen} />
          </div>
          <div className="flex mx-5 mt-2 mb-3 content-center items-center">
            <img className="w-6 mr-3 mb-2" src={laws} alt="Emergency" />
            <div className="flex flex-col md:flex-row">
              <Typography
                onClick={() => setTabSelect("Laws")}
                className="mr-3 cursor-pointer"
                color={"black"}
                variant="p"
              >
                {relLawsTitle} {">"}
              </Typography>
              <Typography
                className={`${classes.headingColor} font-bold`}
                variant="p"
              >
                {getTextFromTranslation(selectedLaw?.title)}
              </Typography>
            </div>
          </div>
          <div className=" hidden md:block">
            <Options handleOpen={handleOpen} />
          </div>
        </div>
        <div className={clsx("flex flex-col text-lg my-2 items-start mx-auto w-full", classes.contentBg)}>
          {selectedLaw?.sections?.length > 0 && (
            <>
              <div className={`${classes.lawsTextBg} w-full py-4 mb-2`}>
                <div className="text-center">
                  <Typography
                    className="self-center text-lg block font-bold"
                    color="white"
                    variant="p"
                  >
                    {penalTitle}
                  </Typography>
                </div>
              </div>
              <div className={clsx("w-full px-4 md:px-0 md:w-8/12 mx-auto max-w-full pb-4", isEnglish ? "text-left" : "text-right")}>
                <Typography
                  className={`${classes.bodyText} my-2 block`}
                  variant="p"
                >
                  {penalDesTitle}
                </Typography>
                {selectedLaw?.sections?.map((sec, secIndex) => (
                  <div
                    key={sec?._id ?? `sec-${secIndex}`}
                    className={clsx(
                      "flex items-start",
                      isEnglish ? "flex-row justify-start" : "flex-row-reverse"
                    )}
                  >
                    <div>
                      <div className={`${classes.circle} mt-4`}></div>
                    </div>
                    <Typography
                      className={`${classes.bodyText} text-base block m-2 ${
                        isEnglish ? "text-left" : "text-right"
                      }`}
                      variant="p"
                    >
                      {getTextFromTranslation(sec)}
                    </Typography>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {selectedLaw?.laws?.length > 0 && (
          <div className={`${classes.lawsTextBg} w-full py-4`}>
            <div className="text-center">
              <Typography
                className="self-center text-lg block font-bold"
                color="white"
                variant="p"
              >
                {lawsTitle}
              </Typography>
            </div>
          </div>
        )}
        <div className={clsx("w-full", classes.contentBg)}>
          <div
            className={clsx(
              "w-full px-4 md:px-0 md:w-8/12 mx-auto max-w-full pt-5 pb-9 flex flex-col",
              isEnglish ? "text-left" : "text-right pr-20"
            )}
          >
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            >
              {Object.keys(lawsProvince).map((key) => (
                <Grid item xs={12} md={6} key={key}>
                  <div className="mb-3">
                    <ul className="">
                      <li className={clsx(classes.bodyText)}>
                        <div
                          className={clsx(
                            "list-disc flex items-start",
                            isEnglish ? "flex-row" : "flex-row-reverse"
                          )}
                        >
                          <div className={`${classes.circle}`}></div>
                          <Typography
                            variant="p"
                            className={clsx(
                              classes.textColor,
                              "block font-bold text-xl mb-1 mx-2"
                            )}
                          >
                            {getProvince(key)}
                          </Typography>
                        </div>
                        {lawsProvince[key]?.map((law, lawIndex) => (
                          <Typography
                            key={law?._id ?? law?.id ?? `law-${lawIndex}`}
                            variant="p"
                            component="span"
                            className={clsx(
                              "text-base font-normal flex items-center mb-2",
                              isEnglish
                                ? "flex-row text-left ml-4"
                                : "flex-row-reverse text-right mr-4"
                            )}
                          >
                            <a
                              href={law.website_link}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: colors.textDark, textDecoration: "underline" }}
                            >
                              {isEnglish ? "- " : ""}{getTextFromTranslation(law.title)}
                              <OpenInNewIcon
                                fontSize="small"
                                sx={{
                                  color: theme.palette.laws.main,
                                  marginLeft: 1,
                                  verticalAlign: "middle",
                                }}
                              />
                            </a>
                          </Typography>
                        ))}
                      </li>
                    </ul>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
          <img
            alt="Arrow Back"
            onClick={() => {
              if (backToGuide) {
                dispatch(setBackToGuide(false));
                setTabSelect("GuideStep");
              } else {
                setTabSelect("Laws");
              }
            }}
            src={ArrowBack}
            className="mt-16 cursor-pointer self-start ml-5"
          />
        </div>
      </div>
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} />
      </div>
    </div>
  );
}

export default RelevantLaws;
